import { useSetting } from '@ui/hooks'
import {
  hasUpsellKey,
  isAbandoned,
  isApplePayActive,
  isMaxItemNegative,
  isMaxQuantityDiscount,
  isMaxQuantityDiscountProduct,
  preCalcOrder,
  priceChangedVariantItemsKey,
  sendMessageToApp
} from '@libs/client/helpers'
import { setGlobalState, useGlobalState, useRefDep } from '@libs/client/hooks'
import { useRouter } from 'next/router'
import {
  CartItem,
  CreatedCart,
  UpdatedCart,
  CartLegacyService,
  UpdatingCartItem,
  CustomerAddress,
  CustomerAddressField,
  BillingAddress,
  CollectTaxRequest,
  PayGoogleService
} from '../services'
import {
  useIsCollectTax,
  useLocalBuyNowOrder,
  useLocalDiscountCode,
  useLocalIsBuyNow,
  useLocalOrder,
  useLocalOrderId,
  useLocalPurchasedOrder,
  useLocalSellpageId,
  useLocalSessionId,
  useLocalSourceEvent
} from './useLocalCart'
import { useTrackingService } from './useTrackingService'
import { countItems } from '@ui/helpers'
import { method_type } from '@libs/common/models/constant'
import { convertNumber, trunc } from '@ui/helpers'
import { Order, OrderItem } from '@ui/types/interfaces'

const getOrderItems = (items: SfTypes.Item[]): CartItem[] =>
  items.map((item) => ({
    id: item.id,
    quantity: item.quantity,
    variant_data: item.variant_data,
    variant_id: item.variant_id
  }))

export const useOrderService = () => {
  const router = useRouter()
  const slug = router.query.subpath?.toString()
  const isAftersale = !!(router.query?.afs ?? '')
  const [localOrderId, setLocalOrderId] = useLocalOrderId()
  const [localOrder, setLocalOrder] = useLocalOrder()
  const [localBuyNowOrder, setLocalBuyNowOrder] = useLocalBuyNowOrder()
  const [isAbandonedOrder] = useGlobalState(isAbandoned)
  const [purchasedOrder] = useLocalPurchasedOrder<Types.Order>()
  const [localIsBuyNow, setLocalIsBuyNow] = useLocalIsBuyNow()
  const localIsBuyNowRef = useRefDep(localIsBuyNow)
  const localBuyNowOrderRef = useRefDep(localBuyNowOrder)
  const [localDiscountCode] = useLocalDiscountCode()
  const [localSourceEvent] = useLocalSourceEvent()
  const slugRef = useRefDep(slug)
  const localOrderIdRef = useRefDep(localOrderId)
  const localDiscountCodeRef = useRefDep(localDiscountCode)
  const localSourceEventRef = useRefDep(localSourceEvent)
  const [storeSetting] = useSetting('sellpage')
  const [localSessionId] = useLocalSessionId()
  const [localSellpageId] = useLocalSellpageId()
  const isSelless = router.query?.s || router.query?.selless
  const { trackingInitCheckout } = useTrackingService()
  const [, setIsCollect] = useIsCollectTax()
  const isThankyouAction = router?.query?.afs
  const [applePayActive] = useGlobalState(isApplePayActive)
  const applePayActiveRef = useRefDep(applePayActive)

  const localOrderRef = useRefDep(localOrder)
  const [, setIsMaxQuantityDiscount] = useGlobalState<number>(isMaxQuantityDiscount)
  const [, setIsMaxQuantityDiscountProduct] = useGlobalState<number>(isMaxQuantityDiscountProduct)
  const [, setIsMaxItemNegative] = useGlobalState<boolean>(isMaxItemNegative)
  const [hasUpsell] = useGlobalState<boolean>(hasUpsellKey)

  const lastCart = countItems(purchasedOrder?.items || []) ?? 0

  const updateLocalData = (
    result: CreatedCart & UpdatedCart,
    isBuyNow: boolean,
    currentItems: SfTypes.Item[]
  ) => {
    setLocalIsBuyNow(isBuyNow ?? false)
    if (result.id) setLocalOrderId(result.id)
    if (result.items) {
      const updatedItems: SfTypes.PriceChangedVariantItems = {}
      currentItems = currentItems.map((item) => {
        const price = result.items?.[item.id]
        if (price !== item.price && item.variant_id)
          updatedItems[item.variant_id] = { default_price: price }
        return {
          ...item,
          price: price ?? item.price
        }
      })
      setGlobalState({ [priceChangedVariantItemsKey(slugRef.current)]: updatedItems })
    }
    setLocalBuyNowOrder((prev: any) => ({
      ...prev,
      ...(result.id ? { id: result.id /*, code: result.code*/ } : {}),
      ...(isBuyNow ? { ...result, items: currentItems } : {})
    }))
    setLocalOrder((prev: any) => ({
      ...prev,
      ...(result.id ? { id: result.id /*, code: result.code*/ } : {}),
      ...(!isBuyNow ? { ...result, items: currentItems } : {})
    }))
  }

  const initItems = async (
    items: SfTypes.Item[],
    isBuyNow?: boolean,
    paygate: 'STRIPE' | 'PAYPALEX' | 'AMAZON' = 'STRIPE',
    pageId?: string,
    slug?: string,
    pageVersion?: string
  ) => {
    if (!items || !items.length) throw new Error('There is no item in order.')
    const orderItems = getOrderItems(items)
    try {
      let service: Promise<CreatedCart & UpdatedCart>
      if (localOrderIdRef.current) {
        service = CartLegacyService.cart1({
          i: localSessionId || '',
          id: localOrderIdRef.current,
          f: isBuyNow ? 'checkout' : 'cart',
          l: encodeURIComponent(window.location.href) || '',
          gw: isBuyNow ? '' : paygate,
          ap: (applePayActiveRef.current as string) || '',
          ...(isThankyouAction ? { lc: lastCart } : {}),
          body: {
            cartItems: orderItems,
            discount_code: localDiscountCodeRef.current || '',
            page_id: pageId || '',
            page_version: pageVersion ? +pageVersion : 0
          }
        })
        if (isSelless) {
          trackingInitCheckout()
        }
      } else {
        service = CartLegacyService.cart({
          i: localSessionId || undefined,
          l: encodeURIComponent(window.location.href) || '',
          f: isBuyNow ? 'checkout' : 'cart',
          gw: isBuyNow ? '' : paygate,
          ap: (applePayActiveRef.current as string) || '',
          ...(isThankyouAction ? { lc: lastCart } : {}),
          body: {
            items: orderItems,
            discount_code: localDiscountCodeRef.current || '',
            page_id: pageId || '',
            slug: slug || '',
            page_version: pageVersion ? +pageVersion : 0
          }
        })
      }
      const result = await service
      if (result.id) {
        sendMessageToApp({
          type: 'INIT_CHECKOUT',
          payload: {
            cartId: result.id
          }
        })
      }
      updateLocalData(result, isBuyNow ?? false, items)
      return { ...result, items: items }
    } catch (err: any) {
      const errCode = err?.response?.data?.status
      if (errCode === 404) {
        const result = await CartLegacyService.cart({
          i: localSessionId || undefined,
          l: encodeURIComponent(window.location.href) || '',
          f: isBuyNow ? 'checkout' : 'cart',
          gw: isBuyNow ? '' : paygate,
          ap: (applePayActiveRef.current as string) || '',
          ...(isThankyouAction ? { lc: lastCart } : {}),
          body: {
            items: orderItems,
            ...(localDiscountCodeRef.current
              ? { discount_code: localDiscountCodeRef.current }
              : {}),
            ...(slugRef.current ? { slug: slugRef.current } : {})
          }
        })
        updateLocalData(result, isBuyNow ?? false, items)
        if (result.id) {
          sendMessageToApp({
            type: 'INIT_CHECKOUT',
            payload: {
              cartId: result.id
            }
          })
        }
        return { ...result, items: items }
      } else {
        throw err
      }
    }
  }

  /**
   * Add new items to cart
   *
   * @param items items to add more to normal cart
   */
  const addItems = (items: SfTypes.Item[]) => {
    let currentItems = []
    if (hasUpsell || isThankyouAction) {
      currentItems = calcOrder([...(localOrder?.items ?? []), ...items])
    } else {
      currentItems = [...(localOrder?.items ?? []), ...items]
    }
    const result = preCalcOrder(currentItems)
    setLocalIsBuyNow(false)
    setLocalOrder({
      ...localOrder,
      ...result,
      items: currentItems
    })
  }

  /**
   * Remove items in cart by item ids
   * @param itemIds
   */
  const removeItems = (itemIds: string[]) => {
    sendMessageToApp({
      type: 'CART_REMOVE_ITEM',
      payload: {
        sellpage_id: localSellpageId || '',
        variant_id: itemIds[0]
      }
    })
    const temp = [...(localOrder?.items ?? [])]
    let items = []
    if (hasUpsell) {
      items = calcOrder(temp.filter((item) => !itemIds.includes(item.id)))
    } else {
      items = temp.filter((item) => !itemIds.includes(item.id))
    }

    const result = preCalcOrder(items)
    setLocalOrder({
      ...localOrder,
      ...result,
      items
    })
    sendMessageToApp({
      type: 'UPDATE_CART',
      ...localOrder,
      ...result,
      items
    })
  }

  /**
   * Update existed items with new quantity
   * @param items
   */
  const updateItems = (items: UpdatingCartItem[]) => {
    if (items.length > 0) {
      sendMessageToApp({
        type: 'CART_CHOOSE_QUANTITY',
        payload: {
          sellpage_id: localSellpageId || ''
        }
      })
      const temp = [...(localOrder?.items ?? [])]
      const tempItems = temp.map((item) => {
        const match = items.find((_item) => _item.id === item.id)
        return match ? { ...item, quantity: match.quantity } : item
      })
      let dataItems = []
      if (hasUpsell || isThankyouAction) {
        dataItems = calcOrder(tempItems)
      } else {
        dataItems = tempItems
      }
      const result = preCalcOrder(dataItems)
      setLocalIsBuyNow(false)
      setLocalOrder({
        ...localOrder,
        ...result,
        items: dataItems
      })
      sendMessageToApp({
        type: 'UPDATE_CART',
        ...localOrder,
        ...result,
        items: dataItems
      })
    }
  }
  const calcOrder = (
    items: OrderItem[],
    isGetItemsProductForm = false,
    lastItemDiscountProp = 0,
    itemIndexProp = 0
  ) => {
    const discountValuePercent = (localOrder as Order).discount_value_percent || 0
    const initiateDiscountPercentUpsell =
      (localOrder as Order).initiate_discount_percent_upsell || 0
    const discountPercentUpsell = (localOrder as Order).discount_percent_upsell || 0

    let quantity = 0
    let isLastItemSameMax = false

    let lastItemDiscount = isThankyouAction ? lastCart - 1 : lastItemDiscountProp
    let itemIndex = isThankyouAction ? lastCart : itemIndexProp

    const itemsTemp = [...items].map((item) => {
      let totalAmount = 0
      let itemDefaultPrice = 0
      const quantityTmp = quantity + (item?.quantity || 0)
      const defaultPrice = item?.default_price || 0
      const minPrice =
        trunc(item?.total_pc || 0, 2) + 2 + trunc((discountValuePercent * defaultPrice) / 100, 2)
      const maxItem = ~~(
        (defaultPrice - minPrice - (initiateDiscountPercentUpsell * defaultPrice) / 100) /
        ((defaultPrice * discountPercentUpsell) / 100)
      )
      setTimeout(() => {
        setIsMaxItemNegative(maxItem <= 0)
      }, 1)
      if (maxItem <= 0) {
        return {
          ...item
        }
      }
      if (lastItemDiscount > maxItem) {
        lastItemDiscount = maxItem
      }
      let itemDefaultPriceTmp = trunc(
        (defaultPrice *
          (100 - (initiateDiscountPercentUpsell + lastItemDiscount * discountPercentUpsell))) /
          100,
        2
      )
      for (let i = 0; i < (item?.quantity || 0); i++) {
        itemIndex++
        // next item
        if (itemIndex > 1) {
          // compare last_item_discount vs max_item_discount
          if (lastItemDiscount < maxItem) {
            lastItemDiscount++
            itemDefaultPriceTmp =
              defaultPrice *
              ((100 - (initiateDiscountPercentUpsell + lastItemDiscount * discountPercentUpsell)) /
                100)
          }
          itemDefaultPrice = trunc(itemDefaultPriceTmp, 2)
        } // first item
        else itemDefaultPrice = defaultPrice
        totalAmount = (totalAmount * 100 + itemDefaultPrice * 100) / 100
      }
      isLastItemSameMax = lastItemDiscount == maxItem
      const price = trunc(convertNumber(totalAmount, item?.quantity || 1), 2)
      quantity = quantityTmp
      return {
        ...item,
        price
      }
    })
    if (isGetItemsProductForm) {
      setIsMaxQuantityDiscountProduct(isLastItemSameMax ? lastItemDiscount : lastItemDiscount)
    } else {
      setIsMaxQuantityDiscount(isLastItemSameMax ? lastItemDiscount : lastItemDiscount + 1)
    }
    return itemsTemp
  }

  const updateShipping = <K extends keyof CustomerAddress>(
    field: CustomerAddressField,
    value: CustomerAddress[K]
  ) => {
    if (localOrderIdRef.current) {
      try {
        const flowType = isAftersale ? 'afs' : isAbandonedOrder ? 'abd' : null
        CartLegacyService.shipping({
          id: localOrderIdRef.current,
          i: localSessionId ?? '',
          l: encodeURIComponent(window.location.href) || '',
          fi: field,
          f: localIsBuyNow ? 'checkout' : 'cart',
          ...(flowType ? { tp: flowType } : {}),
          body: {
            field,
            value
          }
        })
      } catch (err: any) {
        console.error(err)
      }
    }
  }
  const updateTaxAmount = async (
    address: CustomerAddress,
    method: method_type,
    /** Used only on checkout page. Check if user exits Paypal/Amazon Pay popup, and recalculate taxes accordingly. */
    isCancelExternal = false,
    orderId?: string
  ) => {
    const body: CollectTaxRequest = {
      address1: address?.address1 || '',
      address2: address?.address2 || '',
      city: address?.city || '',
      country: address?.country || '',
      country_code: address?.country_code || '',
      postal_code: address?.postal_code || '',
      state: address?.state || '',
      method
    }
    const orderID = orderId || localOrderIdRef.current
    if (orderID) {
      try {
        if (method === method_type.CREDITCARD && !isCancelExternal) {
          setIsCollect(true)
        }
        const res = await CartLegacyService.collectTax({
          f: localIsBuyNowRef.current ? 'checkout' : 'cart',
          id: orderID,
          body
        })
        updateLocalData(
          res,
          localIsBuyNowRef.current ?? false,
          (localIsBuyNowRef.current
            ? localBuyNowOrderRef?.current?.items
            : localOrderRef?.current?.items) as SfTypes.Item[]
        )
        return res
      } catch (err: any) {
        console.error('Error with update Tax', err)
        throw err
      }
    }
  }
  const updateOtpOut = (opt_out: boolean) => {
    if (localOrderIdRef.current) {
      try {
        CartLegacyService.optOut({
          id: localOrderIdRef.current,
          body: {
            opt_out
          }
        })
      } catch (err: any) {
        console.error(err)
      }
    }
  }

  const updateShippingAddress = async (address: CustomerAddress) => {
    if (localOrderIdRef.current) {
      try {
        const id = localOrderIdRef.current
        await Promise.all(
          Object.entries(address).map(async (field) => {
            const [key, value] = field as [CustomerAddressField, string]
            await CartLegacyService.shipping({
              id,
              body: {
                field: key,
                value
              }
            })
          })
        )
      } catch (err: any) {
        console.error(err)
        throw err
      }
    }
  }

  const updateBilling = async (orderId?: string, billing?: BillingAddress) => {
    if (orderId) {
      try {
        await CartLegacyService.billing({
          id: orderId,
          body: billing
        })
      } catch (err: any) {
        console.error(err)
        throw err
      }
    }
  }

  // Apple Pay
  const updateApplePayTaxAmount = async ({
    address,
    orderItems,
    orderId,
    pageVersion,
    pageId,
    slug,
    discountCode
  }: {
    address: CustomerAddress
    orderItems: CartItem[]
    orderId?: string | null
    pageVersion?: number
    pageId?: string
    slug?: string
    discountCode?: string
  }) => {
    if (orderId) {
      try {
        await CartLegacyService.cart1({
          id: orderId,
          i: localSessionId || '',
          f: localIsBuyNowRef.current ? 'checkout' : 'cart',
          l: encodeURIComponent(window.location.href) || '',
          gw: 'apple_pay',
          ap: (applePayActiveRef.current as string) || '',
          ...(isThankyouAction ? { lc: lastCart } : {}),
          body: {
            cartItems: orderItems,
            page_id: pageId || '',
            page_version: pageVersion || 0
          }
        })
        const taxRes = await updateTaxAmount(address, method_type.apple_pay, false, orderId)
        return taxRes
      } catch (err: any) {
        console.error('Error when update order with Apple Pay', err)
        throw err
      }
    } else {
      try {
        const initCart = await CartLegacyService.cart({
          i: localSessionId || undefined,
          l: encodeURIComponent(window.location.href) || '',
          f: localIsBuyNowRef.current ? 'checkout' : 'cart',
          gw: 'apple_pay',
          ap: (applePayActiveRef.current as string) || '',
          ...(isThankyouAction ? { lc: lastCart } : {}),
          body: {
            items: orderItems,
            page_id: pageId || '',
            ...(discountCode ? { discount_code: discountCode } : {}),
            ...(slug ? { slug: slug } : {}),
            ...(pageVersion ? { page_version: pageVersion } : {})
          }
        })
        if (initCart.id) setLocalOrderId(initCart.id)
        const res = await updateTaxAmount(address, method_type.apple_pay, false, initCart.id)
        return res
      } catch (error) {
        console.error('Error when init order with Apple Pay', error)
      }
    }
  }

  const initGooglePay = async ({
    orderItems,
    orderId
  }: {
    orderItems: any[]
    orderId?: string | null
  }) => {
    const items = getOrderItems(orderItems ?? [])
    if (orderId) {
      try {
        const updatedcart = await CartLegacyService.cart1({
          id: orderId,
          i: localSessionId || undefined,
          f: localIsBuyNowRef.current ? 'checkout' : 'cart',
          l: encodeURIComponent(window.location.href) || '',
          gw: 'google_pay',
          ap: '',
          gp: 'active',
          ...(isThankyouAction ? { lc: lastCart } : {}),
          body: {
            cartItems: items,
            page_id:
              storeSetting?.settings?.general?.page_id || storeSetting?.settings?.general?.id || '',
            ...(storeSetting?.settings?.general?.version_id !== null &&
            storeSetting?.settings?.general?.version_id !== undefined
              ? { page_version: +(storeSetting?.settings?.general?.version_id || '0') }
              : {})
          }
        })
        return updatedcart
      } catch (err: any) {
        console.error('Error when update order with Google Pay', err)
        throw err
      }
    } else {
      try {
        const initCart = await CartLegacyService.cart({
          i: localSessionId || undefined,
          l: encodeURIComponent(window.location.href) || '',
          f: localIsBuyNowRef.current ? 'checkout' : 'cart',
          gw: 'google_pay',
          ap: '',
          gp: 'active',
          ...(isThankyouAction ? { lc: lastCart } : {}),
          body: {
            items: items,
            page_id:
              storeSetting?.settings?.general?.page_id || storeSetting?.settings?.general?.id || '',
            ...(localDiscountCodeRef.current
              ? { discount_code: localDiscountCodeRef.current }
              : {}),
            ...(slugRef.current ? { slug: slugRef.current } : {}),
            ...(localSourceEventRef.current
              ? {
                  source_referer: localSourceEventRef.current.referer,
                  source_referer_data: localSourceEventRef.current.referer_data
                }
              : {}),
            ...(storeSetting?.settings?.general?.version_id !== null &&
            storeSetting?.settings?.general?.version_id !== undefined
              ? { page_version: +(storeSetting?.settings?.general?.version_id || '0') }
              : {})
          }
        })
        if (initCart.id) setLocalOrderId(initCart.id)
        return initCart
      } catch (error) {
        console.error('Error when init order with Google Pay', error)
        throw error
      }
    }
  }

  const generateGooglePayJwt = async () => {
    return PayGoogleService.gp()
  }

  return {
    initItems,
    addItems,
    removeItems,
    updateItems,
    updateShipping,
    updateShippingAddress,
    updateBilling,
    updateOtpOut,
    updateTaxAmount,
    calcOrder,
    updateApplePayTaxAmount,
    initGooglePay,
    generateGooglePayJwt
  }
}
